import React from 'react';
import { createPortal } from 'react-dom';
import tw from 'twin.macro';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

const ModalWrapper = styled.div`
  ${tw`fixed inset-0 flex items-center justify-center p-4 sm:p-6 overflow-y-auto`}
  z-index: var(--z-modal);
`;

const Backdrop = styled.div`
  ${tw`fixed inset-0`}
  background-color: rgb(17 24 39 / 0.55);
  backdrop-filter: blur(2px);
  animation: ui-fade-in var(--transition-modal);
`;

const ModalContent = styled.div<{
  $modalHeight?: string;
  $modalWidth?: string;
}>`
  ${tw`
    relative
    box-border flex flex-col mx-auto min-w-0
    w-full
  `}
  z-index: 1;
  background-color: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-overlay);
  animation: ui-pop-in var(--transition-modal);

  max-width: ${({ $modalWidth }) => $modalWidth ?? 'min(520px, calc(100vw - 2rem))'};

  ${({ $modalHeight }) =>
    $modalHeight
      ? css`
          height: ${$modalHeight};
          max-height: min(${$modalHeight}, calc(100vh - 2rem));
        `
      : css`
          max-height: min(90vh, calc(100vh - 2rem));
        `}
`;

const CloseButton = styled.button`
  ${tw`
    absolute top-3 right-3
    z-20 cursor-pointer
    bg-transparent border-none p-0
    flex items-center justify-center
    w-8 h-8 rounded-full
  `}
  color: var(--color-text-secondary);
  transition:
    background-color var(--transition-normal),
    color var(--transition-normal);

  &:hover {
    background-color: var(--color-bg-secondary);
    color: var(--color-text-primary);
  }
`;

const ModalBody = styled.div<{ $fill?: boolean; $align?: 'start' | 'center' }>`
  ${tw`
    w-full box-border
    px-5 pb-5 pt-11 sm:px-6 sm:pb-6
    overflow-y-auto overflow-x-hidden
  `}
  ${props => props.$fill && tw`flex-1 min-h-0`}
  ${props => props.$align === 'center' && tw`flex flex-col items-center text-center`}
`;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Accessible name only. Callers render their own visible heading. */
  title?: string;
  children: React.ReactNode;
  modalHeight?: string;
  modalWidth?: string;
  /** Center child content horizontally (simple dialogs with stacked actions). */
  contentAlign?: 'start' | 'center';
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  modalHeight,
  modalWidth,
  contentAlign = 'start',
}) => {
  if (!isOpen) return null;

  // Rendered at the body so an ancestor with a transform or filter cannot trap
  // the fixed overlay. `data-portal-safe` lets click-outside handlers ignore it.
  return createPortal(
    <ModalWrapper data-portal-safe="true">
      <Backdrop onClick={onClose} aria-hidden />
      <ModalContent
        onClick={(e: { stopPropagation: () => void }) => e.stopPropagation()}
        $modalHeight={modalHeight}
        $modalWidth={modalWidth}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <CloseButton type="button" onClick={onClose} aria-label="Close">
          <FontAwesomeIcon icon={faXmark} />
        </CloseButton>

        <ModalBody $fill={!!modalHeight} $align={contentAlign} className="ui-scroll">
          {children}
        </ModalBody>
      </ModalContent>
    </ModalWrapper>,
    document.body,
  );
};
